<template>
  <div class="columns is-multiline">
    <b-field :label="$t('demandeLogistique.creationOrUpdate.datePlanification')"
      class="column is-4 mgb-0 obligatoire">
      <b-datepicker show-week-number icon="calendar-today"
        v-model="creationDemandeParametre.datePlanification"
        @input="onDatePlanificationChange"
        nearby-selectable-month-days editable>
      </b-datepicker>
    </b-field>
    <b-field :label="$t('demandeLogistique.creationOrUpdate.typeDemande')"
      class="column is-4 mgb-0 obligatoire">
      <b-select v-model="creationDemandeParametre.type" expanded>
        <option
          v-for="item in constantes.TYPE_INTERVENTION"
          :key="item.key"
          :value="item.key">
          {{ $t(item.libelle) }}
        </option>
      </b-select>
    </b-field>
    <b-field v-if="isInterventionTypeTechnique"
      :label="$t('demandeLogistique.creationOrUpdate.typeOt')"
      class="column is-4 mgb-0"
      :class="{ 'obligatoire': isInterventionTypeTechnique }">
      <b-select v-model="creationDemandeParametre.typeOtId" expanded>
        <option :value="null"></option>
        <option
          v-for="item in typesOT"
          :key="item.id"
          :value="item.id">
          {{ item.libelle }}
        </option>
      </b-select>
    </b-field>
    <div v-if="isInterventionTypeTechnique" class="column is-4 mgb-0">
      <user-selector
        v-model="creationDemandeParametre.demandeurId"
        :label="$t('demandeLogistique.creationOrUpdate.demandeur')"
        obligatoire />
    </div>
    <b-field v-if="isInterventionTypeTechnique"
      :label="$t('demandeLogistique.creationOrUpdate.patrimoine')"
      class="column is-4 mgb-0">
      <b-autocomplete v-model="searchPatrimoine"
        @input="getAllPatrimoine"
        @select="selectPatrimoine"
        :data="dataPatrimoine"
        :loading="isFetching"
        field="libelle" icon="magnify">
        <template slot="empty">
          <div v-if="!isFetching">{{ $t('general.autocomplete.noRslt') }}</div>
          <div v-else>{{ $t('general.autocomplete.search') }}</div>
        </template>
        <template slot-scope="props">
          <div v-if="props.option.id" class="dropdown-item is-paddingless"
            :class="{ 'is-disabled is-italic': props.option.disabled }">
            {{ props.option.libelle }}
          </div>
          <div v-else class="dropdown-item is-paddingless
                is-disabled has-text-default has-text-weight-bold is-underline">
            <b>{{ $t(props.option.title) }}</b>
          </div>
        </template>
      </b-autocomplete>
    </b-field>
    <b-field v-if="isInterventionTypeTechnique"
      class="column is-4 mgb-0"
      :label="$t('demandeLogistique.creationOrUpdate.ordreDemandeIntervention')">
      <b-input v-model="creationDemandeParametre.ordreRealisation"
        type="number"
        maxlength="255">
      </b-input>
    </b-field>
    <b-field :label="$t('demandeLogistique.creationOrUpdate.actionLogistique')"
      class="column is-12 mgb-0 obligatoire">
      <b-input v-model="creationDemandeParametre.actionLogistique"
        type="textarea"
        maxlength="255">
      </b-input>
    </b-field>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import userSelector from '@/components/commun/user/userSelector';
import messageErreur from '@/services/general/messageErreur';
import getPatrimoineOnDemandeLogistique from '@/services/gabed/patrimoine/getPatrimoineOnDemandeLogistique';
import * as tourneeConf from '@/configuration/logistique/tournee/tourneeConf';
import { getAllElementPatri, getAllElementPatriDivers } from '@/services/logistique/tourneeModele/addEditTourneeModele/getAllFacesAndReseaux';
import * as interventionConf from '@/configuration/logistique/intervention/interventionConf';

const Societe = createNamespacedHelpers('societe');

export default {
  props: {
    creationDemandeParametre: {
      type: Object,
      required: true,
    },
    typesOT: {
      type: Array,
      required: true,
    },
    demandeLogistique: {
      type: Object,
      required: false,
    },
    commande: {
      type: Object,
      required: false,
    },
    isProduitIsDivers: {
      type: Boolean,
      default: false,
    },
    doAnnonceursHasEmplacementClient: {
      type: Boolean,
      default: false,
    },
    isInterventionTypeTechnique: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    userSelector,
  },
  data() {
    return {
      dataPatrimoine: [],
      searchPatrimoine: null,
      isFetching: false,
      constantes: {
        TYPE_INTERVENTION: interventionConf.TYPE_INTERVENTION,
      },
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
  },
  methods: {
    onDatePlanificationChange(val) {
      this.$emit('onDatePlanificationChange', val);
    },
    getAllPatrimoine: debounce(async function getFaces() {
      try {
        this.dataPatrimoine = [];
        if (!this.searchPatrimoine) {
          this.cleanPatrimmoineData();
          return;
        }
        this.isFetching = true;
        const query = {
          societeId: this.societeActiveId,
          auto: this.searchPatrimoine,
          nonCommercialisable: false,
          contientNumerique: false,
          actif: true,
        };
        if (this.isProduitIsDivers && this.doAnnonceursHasEmplacementClient) {
          query.clientIds = this.commande?.intervenants
            .filter(intervenant => !!intervenant.annonceur)
            .map(annonceur => annonceur.tiersId);
          query.brouillon = null;
          delete query.contientNumerique;
          this.dataPatrimoine = await getAllElementPatriDivers(query);
        } else {
          this.dataPatrimoine = await getAllElementPatri(query);
        }
        this.isFetching = false;
      } catch (e) {
        this.isFetching = false;
        messageErreur(this.$i18n);
        throw e;
      }
    }, 500),
    cleanPatrimmoineData(creationDemandeParametre) {
      creationDemandeParametre.adresse = null;
      creationDemandeParametre.emplacementId = null;
      creationDemandeParametre.dispositifId = null;
      creationDemandeParametre.materielId = null;
      creationDemandeParametre.faceId = null;
    },
    selectPatrimoine(patrimoine) {
      if (!patrimoine?.typeProduit) return;
      const copyCreationDemandeParametre = cloneDeep(this.creationDemandeParametre);
      this.cleanPatrimmoineData(copyCreationDemandeParametre);
      switch (patrimoine.typeProduit) {
        case tourneeConf.TYPE_EMPLACEMENT:
          copyCreationDemandeParametre.adresse = patrimoine.adresse;
          copyCreationDemandeParametre.emplacementId = patrimoine.id;
          break;
        case tourneeConf.TYPE_DISPOSITIF:
          copyCreationDemandeParametre.adresse = patrimoine.adresse;
          copyCreationDemandeParametre.dispositifId = patrimoine.id;
          break;
        case tourneeConf.TYPE_MATERIEL:
          copyCreationDemandeParametre.adresse = patrimoine.adresse;
          copyCreationDemandeParametre.materielId = patrimoine.id;
          break;
        case tourneeConf.TYPE_FACE:
          copyCreationDemandeParametre.adresse = patrimoine.adresse;
          copyCreationDemandeParametre.faceId = patrimoine.id;
          break;
        default:
          break;
      }
      this.$emit('updateCreationDemandeParametre', copyCreationDemandeParametre);
    },
  },
  async mounted() {
    if (this.demandeLogistique) {
      const result = await getPatrimoineOnDemandeLogistique(this.demandeLogistique);
      this.searchPatrimoine = result?.searchPatrimoine || '';
    }
  },
};
</script>
